export interface SharedTimelineViewportAnchor {
  rowKey: string
  offsetWithinRowPx: number
}

export interface SharedTimelineMountedViewportRow {
  rowKey: string
  offsetTopPx: number
  offsetHeightPx: number
}

export function normalizeSharedTimelineViewportAnchor(
  anchor: SharedTimelineViewportAnchor | null | undefined,
): SharedTimelineViewportAnchor | null {
  if (!anchor || typeof anchor !== "object") return null
  if (typeof anchor.rowKey !== "string" || anchor.rowKey.length === 0) return null
  if (
    typeof anchor.offsetWithinRowPx !== "number"
    || !Number.isFinite(anchor.offsetWithinRowPx)
    || anchor.offsetWithinRowPx < 0
  ) {
    return null
  }

  return {
    rowKey: anchor.rowKey,
    offsetWithinRowPx: Math.floor(anchor.offsetWithinRowPx),
  }
}

export function sameSharedTimelineViewportAnchor(args: {
  left: SharedTimelineViewportAnchor | null
  right: SharedTimelineViewportAnchor | null
}): boolean {
  return args.left?.rowKey === args.right?.rowKey
    && (args.left?.offsetWithinRowPx ?? null) === (args.right?.offsetWithinRowPx ?? null)
}

export function deriveSharedTimelineViewportAnchorFromMountedRows(args: {
  orderedMountedRows: SharedTimelineMountedViewportRow[]
  scrollTopPx: number
}): SharedTimelineViewportAnchor | null {
  let previousRow: SharedTimelineMountedViewportRow | null = null

  for (const row of args.orderedMountedRows) {
    if (args.scrollTopPx < row.offsetTopPx) {
      return previousRow
        ? {
          rowKey: row.rowKey,
          offsetWithinRowPx: 0,
        }
        : null
    }

    const rowBottomPx = row.offsetTopPx + row.offsetHeightPx
    if (rowBottomPx <= args.scrollTopPx) {
      previousRow = row
      continue
    }

    return {
      rowKey: row.rowKey,
      offsetWithinRowPx: Math.max(0, Math.floor(args.scrollTopPx - row.offsetTopPx)),
    }
  }

  return null
}

export function deriveSharedTimelineViewportAnchorRestoreScrollTop(args: {
  anchor: SharedTimelineViewportAnchor | null
  orderedMountedRows: SharedTimelineMountedViewportRow[]
}): number | null {
  if (!args.anchor) return null
  const matchingRow = args.orderedMountedRows.find((row) => row.rowKey === args.anchor?.rowKey)
  if (!matchingRow) return null

  const maxOffsetWithinRowPx = Math.max(0, matchingRow.offsetHeightPx - 1)
  const clampedOffsetWithinRowPx = Math.min(args.anchor.offsetWithinRowPx, maxOffsetWithinRowPx)

  return Math.max(0, matchingRow.offsetTopPx + clampedOffsetWithinRowPx)
}
